import type { Column, ProjectTabSnapshot, Relation, Table, TableIndex } from '@/types'
import { getTableHeight, getTableWidth, type TableBounds } from '@/utils/geometry'
import { normCol, normIdx, normRel, normTable } from '@/utils/normalizers'
import { pinAutomaticRelationshipRoutes } from '@/utils/paths'

export interface IncrementalImportOptions {
  removeMissingTables: boolean
  removeMissingColumns: boolean
  removeMissingRelationships: boolean
  preserveTablePositions: boolean
  preserveRelationshipRouting: boolean
}

export interface ColumnDiff {
  tableName: string
  columnName: string
}

export interface ChangedColumnDiff {
  tableName: string
  columnName: string
  before: Column
  after: Column
  changes: string[]
}

export interface IndexDiff {
  tableName: string
  indexName: string
}

export interface ChangedIndexDiff extends IndexDiff {
  before: TableIndex
  after: TableIndex
  changes: string[]
}

export interface ChangedTableDiff {
  tableName: string
  before: Table
  after: Table
  changes: string[]
}

export interface ChangedRelationshipDiff {
  before: Relation
  after: Relation
  changes: string[]
}

export interface ProjectDiff {
  addedTables: Table[]
  modifiedTables: ChangedTableDiff[]
  removedTables: Table[]
  addedColumns: ColumnDiff[]
  removedColumns: ColumnDiff[]
  changedColumns: ChangedColumnDiff[]
  addedIndexes: IndexDiff[]
  removedIndexes: IndexDiff[]
  changedIndexes: ChangedIndexDiff[]
  addedRelationships: Relation[]
  changedRelationships: ChangedRelationshipDiff[]
  removedRelationships: Relation[]
  warnings: string[]
}

export interface IncrementalImportResult {
  project: ProjectTabSnapshot
  diff: ProjectDiff
}

interface TableLookup {
  byPrimary: Map<string, Table>
  byFallback: Map<string, Table>
}

interface Point {
  x: number
  y: number
}

interface Size {
  w: number
  h: number
}

const TABLE_LAYOUT_SPACING = 120
const RADIAL_DIRECTIONS: Array<'right' | 'bottom' | 'left' | 'top'> = ['right', 'bottom', 'left', 'top']

function deepClone<T>(value: T): T {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value)) as T
}

function normalizeProject(project: ProjectTabSnapshot): ProjectTabSnapshot {
  return {
    tables: project.tables.map(normTable),
    rels: project.rels.map(normalizeImportedRelationship),
    groups: deepClone(project.groups ?? {}),
    defaults: deepClone(project.defaults),
    projectName: project.projectName || 'MorphERD',
    dialect: project.dialect ?? 'postgresql',
  }
}

function normalizeName(value: string): string {
  return String(value || '').trim().toLowerCase()
}

function getTableKeys(table: Pick<Table, 'schema' | 'name' | 'fullName' | 'id'>) {
  const schema = normalizeName(table.schema)
  const name = normalizeName(table.name || table.fullName || table.id)
  return {
    primary: schema ? `${schema}.${name}` : name,
    fallback: name,
  }
}

function buildTableLookup(tables: Table[]): TableLookup {
  const byPrimary = new Map<string, Table>()
  const byFallback = new Map<string, Table>()

  tables.forEach((table) => {
    const keys = getTableKeys(table)
    byPrimary.set(keys.primary, table)
    if (!byFallback.has(keys.fallback)) {
      byFallback.set(keys.fallback, table)
    }
  })

  return { byPrimary, byFallback }
}

function findMatchingTable(table: Table, lookup: TableLookup): Table | undefined {
  const keys = getTableKeys(table)
  return lookup.byPrimary.get(keys.primary) ?? lookup.byFallback.get(keys.fallback)
}

export function normalizeImportedRelationship(relationship: Relation): Relation {
  const normalized = normRel(relationship)
  const sourceTable = normalized.fromTableName || normalized.from
  const sourceColumn = normalized.fromColumn || normalized.col
  const targetTable = normalized.toTableName || normalized.to
  const targetColumn = normalized.toColumn || 'id'
  const relationshipKey = normalized.relationshipKey
    || `${sourceTable}.${sourceColumn}->${targetTable}.${targetColumn}`

  return normRel({
    ...normalized,
    constraintName: normalized.constraintName || '',
    relationshipKey,
  })
}

function getRelationshipFallbackKey(rel: Relation): string {
  const normalized = normalizeImportedRelationship(rel)
  return normalizeName(normalized.relationshipKey || '')
}

function getRelationshipMatchKey(rel: Relation): string {
  const normalized = normalizeImportedRelationship(rel)
  const constraintName = normalizeName(normalized.constraintName || '')
  return constraintName || getRelationshipFallbackKey(normalized)
}

function getColumnChanges(current: Column, imported: Column): string[] {
  const changes: string[] = []
  if (current.name !== imported.name) changes.push('name')
  if (current.type !== imported.type) changes.push('type')
  if (current.key !== imported.key) changes.push('key')
  if (!!current.pk !== !!imported.pk) changes.push('primaryKey')
  if (!!current.fk !== !!imported.fk) changes.push('foreignKey')
  if (!!current.uq !== !!imported.uq) changes.push('unique')
  if (current.notNull !== imported.notNull) changes.push('nullable')
  if (current.blank !== imported.blank) changes.push('blank')
  if ((current.desc || '') !== (imported.desc || '')) changes.push('desc')
  if ((current.defaultValue || '') !== (imported.defaultValue || '')) changes.push('defaultValue')
  if ((current.checkExpression || '') !== (imported.checkExpression || '')) changes.push('checkExpression')
  if ((current.logicalType || '') !== (imported.logicalType || '')) changes.push('logicalType')
  return changes
}

function getTableChanges(current: Table, imported: Table): string[] {
  const changes: string[] = []
  if (current.name !== imported.name) changes.push('name')
  if (current.schema !== imported.schema) changes.push('schema')
  if (current.fullName !== imported.fullName) changes.push('fullName')
  if ((current.comment || '') !== (imported.comment || '')) changes.push('comment')
  return changes
}

function getIndexChanges(current: TableIndex, imported: TableIndex): string[] {
  const changes: string[] = []
  if (current.name !== imported.name) changes.push('name')
  if (current.unique !== imported.unique) changes.push('unique')
  if ((current.type || 'btree') !== (imported.type || 'btree')) changes.push('type')
  if (JSON.stringify(current.cols) !== JSON.stringify(imported.cols)) changes.push('columns')
  return changes
}

function getRelationshipChanges(current: Relation, imported: Relation): string[] {
  const changes: string[] = []
  if (current.type !== imported.type) changes.push('type')
  if ((current.constraintName || '') !== (imported.constraintName || '')) changes.push('constraintName')
  if ((current.fromSchema || '') !== (imported.fromSchema || '')) changes.push('fromSchema')
  if ((current.toSchema || '') !== (imported.toSchema || '')) changes.push('toSchema')
  if ((current.fromTableName || current.from) !== (imported.fromTableName || imported.from)) changes.push('fromTable')
  if ((current.toTableName || current.to) !== (imported.toTableName || imported.to)) changes.push('toTable')
  if ((current.fromColumn || current.col) !== (imported.fromColumn || imported.col)) changes.push('fromColumn')
  if ((current.toColumn || 'id') !== (imported.toColumn || 'id')) changes.push('toColumn')
  return changes
}

function getIndexKey(index: TableIndex): string {
  return normalizeName(index.name) || `${normalizeName(index.cols.join(','))}::${index.unique ? '1' : '0'}::${normalizeName(index.type)}`
}

function mergeColumns(
  currentTable: Table,
  importedTable: Table,
  options: IncrementalImportOptions,
  diff: ProjectDiff,
): Column[] {
  const importedByName = new Map(importedTable.cols.map((column) => [normalizeName(column.name), normCol(column)] as const))
  const currentByName = new Map(currentTable.cols.map((column) => [normalizeName(column.name), normCol(column)] as const))
  const merged: Column[] = []

  currentTable.cols.forEach((column) => {
    const key = normalizeName(column.name)
    const importedColumn = importedByName.get(key)

    if (!importedColumn) {
      diff.removedColumns.push({
        tableName: importedTable.fullName || importedTable.name,
        columnName: column.name,
      })
      if (!options.removeMissingColumns) {
        merged.push(normCol(column))
      }
      return
    }

    const changes = getColumnChanges(normCol(column), importedColumn)
    if (changes.length > 0) {
      diff.changedColumns.push({
        tableName: importedTable.fullName || importedTable.name,
        columnName: column.name,
        before: normCol(column),
        after: importedColumn,
        changes,
      })
    }

    // Casamento por nome → preserva a IDENTIDADE (uid) da coluna existente.
    merged.push({ ...importedColumn, id: column.id })
  })

  importedTable.cols.forEach((column) => {
    const key = normalizeName(column.name)
    if (currentByName.has(key)) return
    diff.addedColumns.push({
      tableName: importedTable.fullName || importedTable.name,
      columnName: column.name,
    })
    merged.push(normCol(column))
  })

  return merged
}

function mergeIndexes(
  currentTable: Table,
  importedTable: Table,
  diff: ProjectDiff,
): TableIndex[] {
  const currentByKey = new Map(currentTable.indexes.map((index) => [getIndexKey(index), normIdx(index)] as const))
  const importedByKey = new Map(importedTable.indexes.map((index) => [getIndexKey(index), normIdx(index)] as const))
  const merged: TableIndex[] = []

  importedTable.indexes.forEach((index) => {
    const normalized = normIdx(index)
    const key = getIndexKey(normalized)
    const currentIndex = currentByKey.get(key)

    if (!currentIndex) {
      const sameName = currentTable.indexes.find((item) => normalizeName(item.name) === normalizeName(normalized.name))
      if (sameName) {
        const before = normIdx(sameName)
        const changes = getIndexChanges(before, normalized)
        if (changes.length > 0) {
          diff.changedIndexes.push({
            tableName: importedTable.fullName || importedTable.name,
            indexName: normalized.name,
            before,
            after: normalized,
            changes,
          })
        }
      } else {
        diff.addedIndexes.push({
          tableName: importedTable.fullName || importedTable.name,
          indexName: normalized.name,
        })
      }
    }

    merged.push(normalized)
  })

  currentTable.indexes.forEach((index) => {
    const normalized = normIdx(index)
    if (importedByKey.has(getIndexKey(normalized))) return
    if (importedTable.indexes.some((item) => normalizeName(item.name) === normalizeName(normalized.name))) return
    diff.removedIndexes.push({
      tableName: currentTable.fullName || currentTable.name,
      indexName: normalized.name,
    })
  })

  return merged
}

function toBounds(table: Pick<Table, 'x' | 'y' | 'width' | 'cols'>): TableBounds {
  return {
    x: table.x,
    y: table.y,
    w: getTableWidth(table),
    h: getTableHeight(table),
  }
}

function getTableSize(table: Pick<Table, 'width' | 'cols'>): Size {
  return { w: getTableWidth(table), h: getTableHeight(table) }
}

export function doesTableOverlap(
  table: Pick<Table, 'x' | 'y' | 'width' | 'cols'>,
  otherTables: Pick<Table, 'x' | 'y' | 'width' | 'cols'>[],
  spacing: number,
): boolean {
  const bounds = toBounds(table)
  return otherTables.some((other) => {
    const otherBounds = toBounds(other)
    return !(
      bounds.x + bounds.w + spacing <= otherBounds.x ||
      otherBounds.x + otherBounds.w + spacing <= bounds.x ||
      bounds.y + bounds.h + spacing <= otherBounds.y ||
      otherBounds.y + otherBounds.h + spacing <= bounds.y
    )
  })
}

export function findNearestFreePosition(
  desiredPosition: Point,
  tableSize: Size,
  existingTables: Table[],
  spacing: number,
): Point {
  const occupied = existingTables.map(toBounds)

  const isFree = (x: number, y: number) =>
    occupied.every((other) => (
      x + tableSize.w + spacing <= other.x ||
      other.x + other.w + spacing <= x ||
      y + tableSize.h + spacing <= other.y ||
      other.y + other.h + spacing <= y
    ))

  if (isFree(desiredPosition.x, desiredPosition.y)) {
    return desiredPosition
  }

  const step = Math.max(48, Math.floor(spacing / 2))
  for (let radius = step; radius <= 4000; radius += step) {
    for (let dx = -radius; dx <= radius; dx += step) {
      const positions = [
        { x: desiredPosition.x + dx, y: desiredPosition.y - radius },
        { x: desiredPosition.x + dx, y: desiredPosition.y + radius },
      ]
      for (const position of positions) {
        if (isFree(position.x, position.y)) return position
      }
    }

    for (let dy = -radius + step; dy <= radius - step; dy += step) {
      const positions = [
        { x: desiredPosition.x - radius, y: desiredPosition.y + dy },
        { x: desiredPosition.x + radius, y: desiredPosition.y + dy },
      ]
      for (const position of positions) {
        if (isFree(position.x, position.y)) return position
      }
    }
  }

  return desiredPosition
}

export function findAvailablePositionAroundTable(params: {
  baseTable: Table
  newTable: Table
  existingTables: Table[]
  preferredDirection?: 'left' | 'right' | 'top' | 'bottom'
  spacing: number
}): Point {
  const { baseTable, newTable, existingTables, preferredDirection, spacing } = params
  const baseSize = getTableSize(baseTable)
  const newSize = getTableSize(newTable)
  const directions = preferredDirection
    ? [preferredDirection, ...RADIAL_DIRECTIONS.filter((direction) => direction !== preferredDirection)]
    : RADIAL_DIRECTIONS

  for (const direction of directions) {
    const desired: Point = { x: baseTable.x, y: baseTable.y }
    if (direction === 'right') {
      desired.x = baseTable.x + baseSize.w + spacing
    } else if (direction === 'left') {
      desired.x = baseTable.x - newSize.w - spacing
    } else if (direction === 'bottom') {
      desired.y = baseTable.y + baseSize.h + spacing
    } else {
      desired.y = baseTable.y - newSize.h - spacing
    }

    const next = findNearestFreePosition(desired, newSize, existingTables, spacing)
    const simulated = normTable({ ...newTable, x: next.x, y: next.y })
    if (!doesTableOverlap(simulated, existingTables, spacing)) {
      return next
    }
  }

  return findNearestFreePosition(
    { x: baseTable.x + baseSize.w + spacing, y: baseTable.y },
    newSize,
    existingTables,
    spacing,
  )
}

export function buildRelationshipGraph(
  tables: Table[],
  relationships: Relation[],
): Map<string, Set<string>> {
  const graph = new Map<string, Set<string>>()

  tables.forEach((table) => {
    graph.set(table.id, new Set())
  })

  relationships.forEach((relationship) => {
    if (!graph.has(relationship.from)) graph.set(relationship.from, new Set())
    if (!graph.has(relationship.to)) graph.set(relationship.to, new Set())
    graph.get(relationship.from)?.add(relationship.to)
    graph.get(relationship.to)?.add(relationship.from)
  })

  return graph
}

export function getMostConnectedTable(
  graph: Map<string, Set<string>>,
  onlyTableIds?: string[],
): string | null {
  const allowed = onlyTableIds ? new Set(onlyTableIds) : null
  let selected: string | null = null
  let maxDegree = -1

  graph.forEach((neighbors, tableId) => {
    if (allowed && !allowed.has(tableId)) return
    if (neighbors.size > maxDegree) {
      selected = tableId
      maxDegree = neighbors.size
    }
  })

  return selected
}

function getPlacementStart(tables: Table[]): Point {
  if (!tables.length) {
    return { x: 96, y: 96 }
  }

  const minX = Math.min(...tables.map((table) => table.x))
  const minY = Math.min(...tables.map((table) => table.y))
  const maxX = Math.max(...tables.map((table) => table.x + getTableWidth(table)))
  const maxY = Math.max(...tables.map((table) => table.y + getTableHeight(table)))

  return {
    x: Math.round((minX + maxX) / 2),
    y: Math.round((minY + maxY) / 2),
  }
}

export function normalizeTablePosition(
  table: Table,
  context: {
    existingTables: Table[]
    desiredPosition?: Point
    spacing?: number
  },
): Table {
  const spacing = context.spacing ?? TABLE_LAYOUT_SPACING
  const desiredPosition = context.desiredPosition ?? { x: table.x, y: table.y }
  const freePosition = findNearestFreePosition(desiredPosition, getTableSize(table), context.existingTables, spacing)
  return normTable({
    ...table,
    x: freePosition.x,
    y: freePosition.y,
  })
}

export function calculateImportedTablesLayout(params: {
  currentTables: Table[]
  importedTables: Table[]
  relationships: Relation[]
  spacing?: number
}): Table[] {
  const spacing = params.spacing ?? TABLE_LAYOUT_SPACING
  const positionedCurrent = params.currentTables.map(normTable)
  const pending = new Map(params.importedTables.map((table) => [table.id, normTable(table)] as const))

  if (!pending.size) return []

  const placed: Table[] = []
  const graph = buildRelationshipGraph([...positionedCurrent, ...params.importedTables], params.relationships.map(normalizeImportedRelationship))
  const currentIds = new Set(positionedCurrent.map((table) => table.id))
  const relatedToCurrent = [...pending.values()]
    .filter((table) => [...(graph.get(table.id) ?? [])].some((neighborId) => currentIds.has(neighborId)))
    .sort((left, right) => (graph.get(right.id)?.size ?? 0) - (graph.get(left.id)?.size ?? 0))

  const placeAroundAnchor = (table: Table, anchor: Table) => {
    const allPlaced = [...positionedCurrent, ...placed]
    const position = findAvailablePositionAroundTable({
      baseTable: anchor,
      newTable: table,
      existingTables: allPlaced,
      spacing,
    })
    const normalized = normalizeTablePosition(table, {
      existingTables: allPlaced,
      desiredPosition: position,
      spacing,
    })
    placed.push(normalized)
    pending.delete(table.id)
    return normalized
  }

  relatedToCurrent.forEach((table) => {
    if (!pending.has(table.id)) return
    const anchors = [...(graph.get(table.id) ?? [])]
      .map((neighborId) => positionedCurrent.find((item) => item.id === neighborId))
      .filter((item): item is Table => !!item)
      .sort((left, right) => (graph.get(right.id)?.size ?? 0) - (graph.get(left.id)?.size ?? 0))

    if (anchors[0]) {
      placeAroundAnchor(table, anchors[0])
    }
  })

  const start = getPlacementStart(positionedCurrent)
  while (pending.size) {
    const rootId = getMostConnectedTable(graph, [...pending.keys()]) ?? pending.keys().next().value ?? null
    if (!rootId) break

    const root = pending.get(rootId)
    if (!root) break

    const rootDegree = graph.get(rootId)?.size ?? 0
    const isIsolated = rootDegree === 0
    const desiredPosition = isIsolated
      ? getPlacementStart([...positionedCurrent, ...placed])
      : start

    const anchor = normalizeTablePosition(root, {
      existingTables: [...positionedCurrent, ...placed],
      desiredPosition,
      spacing,
    })
    placed.push(anchor)
    pending.delete(rootId)

    const queue = [anchor]
    const visited = new Set<string>([anchor.id])

    while (queue.length) {
      const base = queue.shift()
      if (!base) continue

      const neighbors = [...(graph.get(base.id) ?? [])]
        .filter((neighborId) => pending.has(neighborId) && !visited.has(neighborId))
        .sort((left, right) => (graph.get(right)?.size ?? 0) - (graph.get(left)?.size ?? 0))

      neighbors.forEach((neighborId, index) => {
        const table = pending.get(neighborId)
        if (!table) return

        const preferredDirection = RADIAL_DIRECTIONS[index % RADIAL_DIRECTIONS.length]
        const position = findAvailablePositionAroundTable({
          baseTable: base,
          newTable: table,
          existingTables: [...positionedCurrent, ...placed],
          preferredDirection,
          spacing,
        })
        const normalized = normalizeTablePosition(table, {
          existingTables: [...positionedCurrent, ...placed],
          desiredPosition: position,
          spacing,
        })
        placed.push(normalized)
        pending.delete(neighborId)
        visited.add(neighborId)
        queue.push(normalized)
      })
    }
  }

  return placed
}

function preserveRelationshipVisuals(current: Relation, imported: Relation, preserveRouting: boolean): Relation {
  return normalizeImportedRelationship({
    ...imported,
    id: current.id,
    mid: preserveRouting ? current.mid : imported.mid,
    pts: preserveRouting ? deepClone(current.pts) : imported.pts,
    fromAnchor: preserveRouting ? deepClone(current.fromAnchor) : imported.fromAnchor,
    toAnchor: preserveRouting ? deepClone(current.toAnchor) : imported.toAnchor,
    waypoints: preserveRouting ? deepClone(current.waypoints ?? []) : imported.waypoints,
    isManual: preserveRouting ? current.isManual : imported.isManual,
  })
}

export function applyIncrementalImport(
  currentProject: ProjectTabSnapshot,
  importedProject: ProjectTabSnapshot,
  options: IncrementalImportOptions,
  importWarnings: string[] = [],
): IncrementalImportResult {
  const current = normalizeProject(currentProject)
  const imported = normalizeProject(importedProject)
  const diff: ProjectDiff = {
    addedTables: [],
    modifiedTables: [],
    removedTables: [],
    addedColumns: [],
    removedColumns: [],
    changedColumns: [],
    addedIndexes: [],
    removedIndexes: [],
    changedIndexes: [],
    addedRelationships: [],
    changedRelationships: [],
    removedRelationships: [],
    warnings: [...importWarnings],
  }

  if (current.dialect !== imported.dialect) {
    diff.warnings.push(`Dialect change: ${current.dialect} -> ${imported.dialect}`)
  }

  const tableLookup = buildTableLookup(current.tables)
  const matchedCurrentIds = new Set<string>()
  const mergedExistingTables: Table[] = []
  const newTables: Table[] = []
  const importedTableIdMap = new Map<string, string>()

  imported.tables.forEach((importedTable) => {
    const currentTable = findMatchingTable(importedTable, tableLookup)
    if (!currentTable) {
      diff.addedTables.push(normTable(importedTable))
      newTables.push(normTable(importedTable))
      importedTableIdMap.set(importedTable.id, importedTable.id)
      return
    }

    matchedCurrentIds.add(currentTable.id)
    importedTableIdMap.set(importedTable.id, currentTable.id)
    importedTableIdMap.set(importedTable.fullName, currentTable.id)
    const tableChanges = getTableChanges(currentTable, importedTable)
    const mergedCols = mergeColumns(currentTable, importedTable, options, diff)
    const mergedIndexes = mergeIndexes(currentTable, importedTable, diff)
    const mergedTable = normTable({
      ...importedTable,
      id: currentTable.id,
      fullName: currentTable.fullName || importedTable.fullName,
      x: options.preserveTablePositions ? currentTable.x : importedTable.x,
      y: options.preserveTablePositions ? currentTable.y : importedTable.y,
      g: currentTable.g,
      comment: importedTable.comment,
      cols: mergedCols,
      indexes: mergedIndexes,
    })

    if (tableChanges.length > 0) {
      diff.modifiedTables.push({
        tableName: currentTable.fullName || currentTable.name,
        before: normTable(currentTable),
        after: mergedTable,
        changes: tableChanges,
      })
    }

    mergedExistingTables.push(mergedTable)
  })

  current.tables.forEach((currentTable) => {
    if (matchedCurrentIds.has(currentTable.id)) return
    diff.removedTables.push(normTable(currentTable))
    if (!options.removeMissingTables) {
      mergedExistingTables.push(normTable(currentTable))
    }
  })

  const layoutRelationships = imported.rels.map(normalizeImportedRelationship)
  const placedNewTables = calculateImportedTablesLayout({
    currentTables: mergedExistingTables,
    importedTables: newTables,
    relationships: layoutRelationships.map((relationship) => normalizeImportedRelationship({
      ...relationship,
      from: importedTableIdMap.get(relationship.from) || relationship.from,
      to: importedTableIdMap.get(relationship.to) || relationship.to,
    })),
  })
  const mergedTables = [...mergedExistingTables, ...placedNewTables]
  const availableTableIds = new Set(mergedTables.map((table) => table.id))

  const currentByKey = new Map(current.rels.map((rel) => [getRelationshipMatchKey(rel), normalizeImportedRelationship(rel)] as const))
  const currentByFallback = new Map(current.rels.map((rel) => [getRelationshipFallbackKey(rel), normalizeImportedRelationship(rel)] as const))
  const importedByKey = new Map(imported.rels.map((rel) => [getRelationshipMatchKey(rel), normalizeImportedRelationship(rel)] as const))
  const mergedRelationships: Relation[] = []
  const matchedRelationshipKeys = new Set<string>()

  imported.rels.forEach((relationship) => {
    const importedRel = normalizeImportedRelationship({
      ...relationship,
      from: importedTableIdMap.get(relationship.from) || relationship.from,
      to: importedTableIdMap.get(relationship.to) || relationship.to,
    })
    if (!availableTableIds.has(importedRel.from) || !availableTableIds.has(importedRel.to)) {
      diff.warnings.push(`Relationship skipped because one of the tables is missing: ${importedRel.relationshipKey}`)
      return
    }

    const matchKey = getRelationshipMatchKey(importedRel)
    const fallbackKey = getRelationshipFallbackKey(importedRel)
    const currentRel = currentByKey.get(matchKey) ?? currentByFallback.get(fallbackKey)

    if (!currentRel) {
      diff.addedRelationships.push(importedRel)
      mergedRelationships.push(importedRel)
      return
    }

    matchedRelationshipKeys.add(getRelationshipMatchKey(currentRel))
    matchedRelationshipKeys.add(getRelationshipFallbackKey(currentRel))

    const changes = getRelationshipChanges(currentRel, importedRel)
    if (changes.length > 0) {
      diff.changedRelationships.push({
        before: currentRel,
        after: importedRel,
        changes,
      })
    }

    mergedRelationships.push(
      preserveRelationshipVisuals(currentRel, importedRel, options.preserveRelationshipRouting),
    )
  })

  current.rels.forEach((relationship) => {
    const currentRel = normalizeImportedRelationship(relationship)
    const matchKey = getRelationshipMatchKey(currentRel)
    const fallbackKey = getRelationshipFallbackKey(currentRel)
    if (matchedRelationshipKeys.has(matchKey) || matchedRelationshipKeys.has(fallbackKey) || importedByKey.has(matchKey)) return
    diff.removedRelationships.push(currentRel)
    if (!options.removeMissingRelationships) {
      mergedRelationships.push(currentRel)
    }
  })

  return {
    project: {
      tables: mergedTables,
      rels: pinAutomaticRelationshipRoutes(mergedRelationships, mergedTables),
      groups: deepClone(current.groups),
      defaults: deepClone(current.defaults),
      projectName: current.projectName,
      dialect: imported.dialect,
    },
    diff,
  }
}
